import { createContext, useContext, useMemo } from 'react';

import { Money } from '@betterment/core';
import { createConfig, createFlowContext } from '@betterment/shared-components';
import { Outlet, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { LegalAccountTaxationType, ManagedSubAccount } from '../graphql/schema';

type TaxCoordinationAccountOption = {
  id: string;
  envelopeAccount: {
    id?: string;
    isTaxCoordinated?: boolean;
    portfolio?: {
      configurationDescription: string;
      strategy: {
        name: string;
        descriptionHtml?: string | null;
      };
    };
    subAccounts?: ManagedSubAccount[];
  };
};

const MOCK_TAX_COORDINATION_ACCOUNT_OPTIONS: TaxCoordinationAccountOption[] = [
  {
    id: '1',
    envelopeAccount: {
      id: 'envelopeAccountId-1',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Core (90% stocks)',
        strategy: { descriptionHtml: 'Globally diverse with broad market exposure', name: 'Core' },
      },
      subAccounts: [
        {
          id: 'traditional-ira-id',
          balance: Money(109251),
          name: 'Betterment - Traditional IRA',
          legalAccount: { taxationType: LegalAccountTaxationType.TraditionalIra },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '2',
    envelopeAccount: {
      id: 'envelopeAccountId-2',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Core (90% stocks)',
        strategy: { descriptionHtml: 'Globally diverse with broad market exposure', name: 'Core' },
      },
      subAccounts: [
        {
          id: 'roth-ira-id',
          balance: Money(12441),
          name: 'Betterment - Roth IRA',
          legalAccount: { taxationType: LegalAccountTaxationType.RothIra },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '3',
    envelopeAccount: {
      id: 'envelopeAccountId-3',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Core (90% stocks)',
        strategy: { descriptionHtml: 'Globally diverse with broad market exposure', name: 'Core' },
      },
      subAccounts: [
        {
          id: 'sep-ira-id',
          balance: Money(917134),
          name: 'Betterment - SEP IRA',
          legalAccount: { taxationType: LegalAccountTaxationType.SepIra },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '4',
    envelopeAccount: {
      id: 'envelopeAccountId-4',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Social Impact (90% stocks)',
        strategy: { descriptionHtml: 'Investments that promote social empowerment', name: 'Social Impact' },
      },
      subAccounts: [
        {
          id: 'taxable-foo-id',
          balance: Money(7543),
          name: 'Taxable Foo',
          legalAccount: { taxationType: LegalAccountTaxationType.Taxable },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '5',
    envelopeAccount: {
      id: 'envelopeAccountId-5',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Core (90% stocks)',
        strategy: { descriptionHtml: 'Globally diverse with broad market exposure', name: 'Core' },
      },
      subAccounts: [
        {
          id: 'traditional-401k-betterment-id',
          balance: Money(135),
          name: 'Retirement - Traditional 401(k) | Betterment Holdings, Inc.',
          legalAccount: { taxationType: LegalAccountTaxationType.Traditional_401K },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '6',
    envelopeAccount: {
      id: 'envelopeAccountId-6',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Social Impact (90% stocks)',
        strategy: { descriptionHtml: 'Investments that promote social empowerment', name: 'Social Impact' },
      },
      subAccounts: [
        {
          id: 'traditional-401k-enchanted-id',
          balance: Money(524513),
          name: 'Retirement - Traditional 401(k) | Enchanted Fairies',
          legalAccount: { taxationType: LegalAccountTaxationType.Traditional_401K },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '7',
    envelopeAccount: {
      id: 'envelopeAccountId-7',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Social Impact (90% stocks)',
        strategy: { descriptionHtml: 'Investments that promote social empowerment', name: 'Social Impact' },
      },
      subAccounts: [
        {
          id: 'roth-401k-betterment-id',
          balance: Money(1412),
          name: 'Retirement - Roth 401(k) | Betterment Holdings, Inc.',
          legalAccount: { taxationType: LegalAccountTaxationType.Roth_401K },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '8',
    envelopeAccount: {
      id: 'envelopeAccountId-8',
      isTaxCoordinated: false,
      portfolio: {
        configurationDescription: 'Core (90% stocks)',
        strategy: { descriptionHtml: 'Globally diverse with broad market exposure', name: 'Core' },
      },
      subAccounts: [
        {
          id: 'roth-401k-enchanted-id',
          balance: Money(0),
          name: 'Retirement - Roth 401(k) | Enchanted Fairies',
          legalAccount: { taxationType: LegalAccountTaxationType.Roth_401K },
        } as ManagedSubAccount,
      ],
    },
  },
  {
    id: '9',
    envelopeAccount: {
      id: 'envelopeAccountId-9',
      isTaxCoordinated: true,
      portfolio: {
        configurationDescription: 'Social Impact (90% stocks)',
        strategy: { descriptionHtml: 'Investments that promote social empowerment', name: 'Social Impact' },
      },
      subAccounts: [
        {
          id: 'hsa-id',
          balance: Money(3515),
          name: 'Health Saving Investments - Health Savings',
          legalAccount: { taxationType: LegalAccountTaxationType.Hsa },
        } as ManagedSubAccount,
        {
          id: 'major-purchase-id',
          balance: Money(746523),
          name: 'Major Purchase - Automated Investing',
          legalAccount: { taxationType: LegalAccountTaxationType.Taxable },
        } as ManagedSubAccount,
      ],
    },
  },
];

type TaxCoordinatedFlowContextValue = {
  taxCoordinationAccountOptions: TaxCoordinationAccountOption[];
};

const TaxCoordinatedFlowContext = createContext<TaxCoordinatedFlowContextValue>({
  taxCoordinationAccountOptions: MOCK_TAX_COORDINATION_ACCOUNT_OPTIONS,
});

export const useTaxCoordinatedPortfolioFlowContext = () => useContext(TaxCoordinatedFlowContext);

export const TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG = createConfig({
  enablement_intent: {
    schema: Yup.object({
      hasIntent: Yup.boolean(),
    }),
    dependencies: Yup.object({}),
  },
  accounts_selection: {
    schema: Yup.object(
      Object.fromEntries(Object.values(LegalAccountTaxationType).map(key => [key, Yup.string()])) as Record<
        LegalAccountTaxationType,
        Yup.StringSchema
      >
    ),
    dependencies: Yup.object({
      enablement_intent: Yup.object({
        hasIntent: Yup.boolean().isTrue().required(),
      }),
    }),
  },
  portfolio_selection: {
    schema: Yup.object({
      portfolioOption: Yup.string(),
    }),
    dependencies: Yup.object({
      enablement_intent: Yup.object({
        hasIntent: Yup.boolean().isTrue().required(),
      }),
      accounts_selection: Yup.object(
        Object.fromEntries(Object.values(LegalAccountTaxationType).map(key => [key, Yup.string()])) as Record<
          LegalAccountTaxationType,
          Yup.StringSchema
        >
      ),
    }),
  },
});

export const { FlowProvider, useStep: useTaxCoordinatedPortfolioFlowStep } = createFlowContext(
  TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG
);

export const TaxCoordinatedPortfolioFlow = () => {
  const { flowId } = useParams<{ flowId: string }>();

  const value = useMemo(() => ({ taxCoordinationAccountOptions: MOCK_TAX_COORDINATION_ACCOUNT_OPTIONS }), []);

  return (
    <TaxCoordinatedFlowContext.Provider value={value}>
      <FlowProvider flowId={flowId}>
        <Outlet />
      </FlowProvider>
    </TaxCoordinatedFlowContext.Provider>
  );
};
